import { useEffect, useRef, useState } from "react";

export function validYouTubeId(value) {
  return /^[A-Za-z0-9_-]{11}$/.test(value || "") ? value : null;
}

function VideoPreview({ video, onOpen }) {
  const [failed, setFailed] = useState(false);
  const id = validYouTubeId(video.id);
  if (!id) return null;

  return (
    <button
      type="button"
      onClick={(event) => onOpen(video, event.currentTarget)}
      aria-label={`Play ${video.title}`}
      className="sn-block sn-w-full sn-text-left"
    >
      <span className="sn-relative sn-flex sn-aspect-video sn-w-full sn-items-center sn-justify-center sn-overflow-hidden sn-rounded-card sn-bg-ink">
        {!failed && (
          <img
            src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="sn-absolute sn-inset-0 sn-h-full sn-w-full sn-object-cover"
          />
        )}
        <span className="sn-absolute sn-inset-0 sn-bg-black/25" />
        <span className="sn-relative sn-flex sn-h-14 sn-w-14 sn-items-center sn-justify-center sn-rounded-full sn-bg-cream sn-text-forest">
          <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="m9 5 11 7-11 7z" />
          </svg>
        </span>
        {failed && <span className="sn-absolute sn-bottom-4 sn-text-sm sn-text-white">Watch on YouTube</span>}
      </span>
      <span className="sn-mt-4 sn-block sn-font-display sn-text-xl sn-text-forest">{video.heading || video.title}</span>
    </button>
  );
}

export default function VideoPreviewGrid({ videos = [], title = "Product video", columns = "md:sn-grid-cols-2" }) {
  const [active, setActive] = useState(null);
  const dialog = useRef(null);
  const origin = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current.showModal();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [active]);

  const close = () => {
    dialog.current.close();
    setActive(null);
    origin.current?.focus();
  };

  if (!videos.length) return null;

  return (
    <>
      <div className={`sn-grid sn-gap-6 ${columns}`}>
        {videos.map((video) => (
          <VideoPreview
            key={video.id}
            video={video}
            onOpen={(item, trigger) => {
              origin.current = trigger;
              setActive(item);
            }}
          />
        ))}
      </div>
      <dialog
        ref={dialog}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const first = dialog.current.querySelector("button");
          const last = dialog.current.querySelector("a");
          if (event.shiftKey && event.target === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && event.target === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        aria-labelledby="sn-video-title"
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClick={(event) => {
          if (event.target !== dialog.current) return;
          const bounds = dialog.current.getBoundingClientRect();
          if (
            event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom
          ) {
            close();
          }
        }}
        className="sn-video-dialog sn-m-auto sn-w-[calc(100%-2rem)] sn-max-w-4xl sn-max-h-[calc(100dvh-2rem)] sn-overflow-y-auto sn-rounded-card sn-bg-cream sn-p-4 sn-text-ink md:sn-p-6"
      >
        {active && (
          <>
            <div className="sn-mb-4 sn-flex sn-items-center sn-justify-between sn-gap-4">
              <h2 id="sn-video-title" className="sn-font-display sn-text-lg sn-text-forest">{active.title || title}</h2>
              <button type="button" autoFocus onClick={close} className="sn-min-h-[44px] sn-min-w-[64px] sn-font-semibold sn-text-forest">Close</button>
            </div>
            <iframe
              className="sn-block sn-aspect-video sn-w-full"
              src={`https://www.youtube.com/embed/${validYouTubeId(active.id)}?autoplay=1`}
              title={active.title || title}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <a
              className="sn-mt-3 sn-inline-flex sn-min-h-[44px] sn-items-center sn-text-sm sn-text-forest sn-underline"
              href={`https://www.youtube.com/watch?v=${active.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open on YouTube (new tab)
            </a>
          </>
        )}
      </dialog>
    </>
  );
}
